"use client"

import { useState, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PenSquare, Trash2, Plus, Search, X, Lock, Loader2 } from "lucide-react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { EditFieldModal } from "@/components/edit-field-modal"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { useLanguage } from "@/context/language-context"
import { useWorkflowTracking } from "@/hooks/use-workflow-tracking"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { getLocalizedField } from "@/utils/language-utils"

interface ManageCustomFieldsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ManageCustomFieldsModal({ isOpen, onClose }: ManageCustomFieldsModalProps) {
  const { configuration, deleteColumn, refreshConfiguration } = useWorkflow()
  const { deleteFieldConfiguration } = useWorkflowApi()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { trackColumnAction } = useWorkflowTracking()

  const [searchTerm, setSearchTerm] = useState("")
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [editingColumn, setEditingColumn] = useState<any | null>(null)
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false)
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false)

  const authorizedActions = useMemo(
    () => (configuration?.authorizedActions || []).map((a: any) => (typeof a === "string" ? a : a.actionCode)),
    [configuration?.authorizedActions]
  )
  const canEditCustomColumns = authorizedActions.includes("EditColumns")
  const canDeleteCustomColumns = authorizedActions.includes("DeleteCustomColumns")

  // All custom fields that are in use (isCustomField = true, present in fieldsInUse)
  const customFields = useMemo(() => {
    const all = configuration?.fieldsInUse || []
    return all
      .filter((f) => f.isCustomField === true)
      .filter((f) => {
        if (!searchTerm.trim()) return true
        const name = getLocalizedField(f, "columnName", language) ?? ""
        return typeof name === "string" && name.toLowerCase().includes(searchTerm.toLowerCase())
      })
      .sort((a, b) => {
        const aName = (getLocalizedField(a, "columnName", language) as string) ?? ""
        const bName = (getLocalizedField(b, "columnName", language) as string) ?? ""
        return aName.localeCompare(bName)
      })
  }, [configuration?.fieldsInUse, searchTerm, language])

  const columnToDeleteName = useMemo(() => {
    if (!columnToDelete) return ""
    const field = (configuration?.fieldsInUse || []).find((f) => f.fieldConfigurationId === columnToDelete)
    return field ? ((getLocalizedField(field, "columnName", language) as string) ?? "") : ""
  }, [columnToDelete, configuration?.fieldsInUse, language])

  const handleDeleteClick = (fieldConfigId: string) => {
    setColumnToDelete(fieldConfigId)
  }

  const confirmDelete = async () => {
    if (!columnToDelete) return
    setDeleting(true)
    try {
      const field = (configuration?.fieldsInUse || []).find(
        (f) => f.fieldConfigurationId === columnToDelete
      )
      const fieldName = field ? (getLocalizedField(field, "columnName", language) as string) : "Unknown Field"

      await deleteFieldConfiguration(columnToDelete)
      await refreshConfiguration(true)
      deleteColumn(columnToDelete)
      trackColumnAction("delete", fieldName)
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("error"),
        description: error instanceof Error ? error.message : t("deleteFailed"),
      })
    } finally {
      setDeleting(false)
      setColumnToDelete(null)
    }
  }

  const handleClose = () => {
    setSearchTerm("")
    onClose()
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[520px] flex flex-col h-[520px] min-h-[520px] max-h-[80vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t("manageCustomFields") || "Manage custom fields"}</DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 focus-visible:ring-0"
            />
            {searchTerm && (
              <button
                type="button"
                aria-label={t("clearSearch")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={() => setSearchTerm("")}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Field list */}
          <div className="flex-1 overflow-y-auto border rounded-md min-h-0">
            {customFields.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-sm text-gray-400 italic">
                {searchTerm
                  ? t("noRecordsFound") || "No fields match your search."
                  : t("noCustomFieldsAvailable") || "No custom fields found."}
              </div>
            ) : (
              customFields.map((field) => {
                const displayName = getLocalizedField(field, "columnName", language) as string
                return (
                  <div
                    key={field.fieldConfigurationId}
                    className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {field.isPrivate && <Lock size={12} className="text-gray-400 flex-shrink-0" />}
                      <span
                        className="truncate text-sm text-gray-800"
                        title={displayName}
                      >
                        {displayName}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!canEditCustomColumns}
                        onClick={() => {
                          setEditingColumn(field)
                          setIsEditFieldModalOpen(true)
                        }}
                        title={t("edit")}
                        aria-label={t("edit")}
                      >
                        <PenSquare size={14} className="text-gray-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!canDeleteCustomColumns}
                        onClick={() => handleDeleteClick(field.fieldConfigurationId)}
                        title={t("delete")}
                        aria-label={t("delete")}
                      >
                        <Trash2
                          size={14}
                          className={canDeleteCustomColumns ? "text-gray-400" : "text-gray-300"}
                        />
                      </Button>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t flex-shrink-0">
            <Button
              variant="ghost"
              className="text-blue-600 flex items-center gap-1"
              onClick={() => setIsAddFieldModalOpen(true)}
              disabled={!canEditCustomColumns}
            >
              <Plus size={16} />
              {t("addNewField") || "+ Custom field"}
            </Button>
            <Button variant="elliptical-outline" onClick={handleClose}>
              {t("close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      {columnToDelete && (
        <AlertDialog open={!!columnToDelete} onOpenChange={(open) => { if (!open && !deleting) setColumnToDelete(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("deleteCustomFieldConfirmTitle", { name: columnToDeleteName })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("deleteCustomFieldConfirmBody", { name: columnToDeleteName })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel variant="elliptical-outline" disabled={deleting}>{t("cancel")}</AlertDialogCancel>
              <Button
                variant="elliptical"
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
              >
                {deleting ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    {t("processing")}
                  </>
                ) : t("delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Edit existing field */}
      {isEditFieldModalOpen && editingColumn && (
        <EditFieldModal
          isOpen={isEditFieldModalOpen}
          onClose={() => {
            setIsEditFieldModalOpen(false)
            setEditingColumn(null)
          }}
          fieldConfiguration={editingColumn}
          canEditCustomColumns={canEditCustomColumns}
        />
      )}

      {/* Add new field */}
      {isAddFieldModalOpen && (
        <EditFieldModal
          isOpen={isAddFieldModalOpen}
          onClose={() => setIsAddFieldModalOpen(false)}
          addMode={true}
          canEditCustomColumns={canEditCustomColumns}
        />
      )}
    </>
  )
}
